import _ from 'lodash';
import { produce } from 'immer';
import { GroupInventoryItem } from '../models/User';
import Leverage from '../components/rushGame/leverage.component';
import { LeverageValues } from '../models/RushGame';

export interface PowerUpItem {
  code: string,
  value: number,
  quantity: number,
}

const gameEngineSetup = {
  defaultTradePrice: 0,
  defaultTradeAmount: 100,
  defaultCountDownTime:20,
  defaultCurrentTime:20,
  defaultGemPrice: 1,
  defaultTradeLeverage: LeverageValues.VALUE_1,
  gemIncreasedTime: 10,
}

const emptyPowerUp: GroupInventoryItem = {
  family: '',
  subFamily: '',
  label:'',
  code: '',
  image1: '',
  quantity: 0,
}

export interface RushGameState {
  isPlaying: boolean,
  isTradingUp: boolean,
  isWinning: boolean,
  tradePrice: number,
  tradeAmount: number,
  tradeLeverage: number,
  tradePowerUp: GroupInventoryItem,
  countDownMaxTime: number,
  countDownCurrentTime: number,
  gemPrice: number,
}

export const rushGameInitialState: RushGameState = {
  isPlaying: false,
  isTradingUp: false,
  isWinning: true,
  tradePrice: gameEngineSetup.defaultTradePrice,
  tradeAmount: gameEngineSetup.defaultTradeAmount,
  tradeLeverage: gameEngineSetup.defaultTradeLeverage,
  tradePowerUp: _.clone(emptyPowerUp),
  countDownMaxTime: gameEngineSetup.defaultCountDownTime,
  countDownCurrentTime: gameEngineSetup.defaultCurrentTime,
  gemPrice: gameEngineSetup.defaultGemPrice,
};

type Action =
  | { type: 'SET_AMOUNT'; payload: number; }
  | { type: 'SET_LEVERAGE'; payload: number; }
  | { type: 'ADD_POWER_UP'; payload: GroupInventoryItem; }
  | { type: 'REMOVE_POWER_UP'; }
  | { type: 'DECREASE_POWER_UP'; }
  | { type: 'START_GAME'; payload: {isTradingUp: boolean, socket:any}; }
  | { type: 'LOAD_GAME'; payload: RushGameState; }
  | { type: 'END_GAME'; }
  | { type: 'EXTEND_GAME'; payload: RushGameState; }
  | { type: 'TICK'; payload: number; };

export const rushGameReducer = (state:RushGameState = rushGameInitialState, action: Action) =>
  produce(state, (engine) => {
    switch (action.type) {
      case 'SET_AMOUNT':
        engine.tradeAmount = action.payload;
        break;
      case 'SET_LEVERAGE':
        engine.tradeLeverage = action.payload;
        break;
      case 'ADD_POWER_UP':
        engine.tradePowerUp = action.payload;
        break;
      case 'REMOVE_POWER_UP':
        engine.tradePowerUp = _.clone(emptyPowerUp);
        break;
      case 'DECREASE_POWER_UP':
        if(engine.tradePowerUp.quantity > 1) {
          engine.tradePowerUp.quantity -= 1;
        } else {
          engine.tradePowerUp = _.clone(emptyPowerUp);
        }
        break;
      case 'START_GAME':
        engine.isTradingUp = action.payload.isTradingUp;
        engine.isPlaying = true;
        // engine.tradePrice is added server side
        action.payload.socket.emit("start-game", engine);
        break;
      case 'LOAD_GAME':
        engine = _.assign( engine, action.payload );
        break;
      case 'END_GAME':
        engine.isPlaying = false;
        engine.isWinning = true;
        engine.countDownCurrentTime = gameEngineSetup.defaultCountDownTime;
        engine.countDownMaxTime = gameEngineSetup.defaultCountDownTime;
        engine.tradePrice = gameEngineSetup.defaultTradePrice;
        break;
      case 'TICK':
        if(engine.isPlaying) {

          engine.countDownCurrentTime -= 1;

          // Patch: red countdown if time is less than 5
          if(engine.countDownCurrentTime <= 5) {
            engine.isWinning = false;
          } else {
            engine.isWinning = true;
          }

        }
        if (engine.countDownCurrentTime <= 0) {
          engine.isPlaying = false;
          engine.isWinning = true;
          engine.countDownCurrentTime = gameEngineSetup.defaultCountDownTime;
          engine.countDownMaxTime = gameEngineSetup.defaultCountDownTime;
          engine.tradePrice = gameEngineSetup.defaultTradePrice;
        }
        break;
      case 'EXTEND_GAME':
        engine.countDownCurrentTime = action.payload.countDownCurrentTime;
        engine.countDownMaxTime = action.payload.countDownMaxTime;
        break;
      default:
        return state;
    }
  });

/* ACTIONS */

export const setAmountAction = (newAmount: number): Action => ({
  type: 'SET_AMOUNT',
  payload: newAmount,
});

export const setLeverageAction = (newLeverage: number): Action => ({
  type: 'SET_LEVERAGE',
  payload: newLeverage,
});

export const addPowerUpAction = (newPowerUp: GroupInventoryItem): Action => ({
  type: 'ADD_POWER_UP',
  payload: newPowerUp,
});

export const removePowerUpAction = (): Action => ({
  type: 'REMOVE_POWER_UP',
});

export const decreasePowerUpAction = (): Action => ({
  type: 'DECREASE_POWER_UP',
});

export const startGameAction = (isTradingUp: boolean, socket:any): Action => ({
  type: 'START_GAME',
  payload: {
    isTradingUp: isTradingUp,
    socket: socket,
  }
});

export const loadGameAction = (rushGameState: RushGameState): Action => ({
  type: 'LOAD_GAME',
  payload: rushGameState,
});

export const endGameAction = (): Action => ({
  type: 'END_GAME',
});

export const extendGameAction = (rushGameState: RushGameState): Action => ({
  type: 'EXTEND_GAME',
  payload: rushGameState,
});

export const tickGameAction = (tradePrice: number): Action => ({
  type: 'TICK',
  payload: tradePrice
});
